import React, { useContext, useEffect, useState } from 'react';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import AppContext from '../../Services/AppContext';
import PersistantData from '../../Services/PersistantData';
import Button from '@material-ui/core/Button';
import { useCommonStyles } from '../../Styles';
import {
	empty,
	req_report_placestracking,
	req_report_placestracking_companies
} from '../../Helper/Constants';
import API_Service, { axiosDefaultConfig } from '../../Services/API_Service';
import { useSnackbar } from 'notistack';
import MUIDataTable from 'mui-datatables';
import { CircularProgress, Paper, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import format from 'date-fns/format';
import moment from 'moment';
import { Skeleton } from '@material-ui/lab';
import LinearLoading from '../../Components/LinearLoading';

const useStyles = makeStyles((theme) => ({
	viewInRow: {
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'left',
		marginTop: theme.spacing(1),
		rowGap: 10
	},
	customDatePicker: {
		"& .MuiOutlinedInput-root": {
			height: "100%",
		},
		"& .MuiOutlinedInput-root .MuiOutlinedInput-input": {
			padding: "5px 15px",
			fontSize: 12,
			textAlign: "center",
		},
		width: 300,
		marginRight: 10,
		fontSize: 12,
	},
	companyAutocomplete: {
		width: 300,
		marginRight: 10,
		'& .MuiInputBase-root': {
			fontSize: 12,
		},
	},
	darkListbox: {
		'&::-webkit-scrollbar': {
			width: 6,
		},
		'&::-webkit-scrollbar-track': {
			background: 'transparent',
		},
		'&::-webkit-scrollbar-thumb': {
			background: 'rgba(255, 255, 255, 0.2)',
			borderRadius: 3,
		},
		'&::-webkit-scrollbar-thumb:hover': {
			background: 'rgba(255, 255, 255, 0.35)',
		},
	},
	searchButton: {
		marginRight: 10,
	},
	spacer: {
		height: theme.spacing(2.5),
	},
	filterPaper: {
		padding: theme.spacing(2),
		marginBottom: theme.spacing(1),
	},
	skeletonFilterRow: {
		display: 'flex',
		gap: 10,
		marginBottom: theme.spacing(2),
	},
	skeletonTableHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		marginBottom: theme.spacing(1),
		padding: theme.spacing(0, 2),
	},
	skeletonRow: {
		display: 'flex',
		justifyContent: 'space-between',
		padding: theme.spacing(1.5, 2),
		borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
	},
}));

export default function RPlacesTracking() {
	const commonStyle = useCommonStyles();
	const classes = useStyles();

	const singleton = PersistantData.getInstance();
	const [gState] = useContext(AppContext);
	var { translation: language, client } = gState;
	if (typeof (language) === 'undefined') {
		language = singleton.getlang();
	}

	const { enqueueSnackbar } = useSnackbar();

	const [companiesList, setCompaniesList] = useState([]);
	const [companiesLoading, setCompaniesLoading] = useState(true);
	const [selectedCompany, setSelectedCompany] = useState(empty);
	const [selectedMonth, setSelectedMonth] = useState(new Date());

	const [reportData, setReportData] = useState([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (client) {
			loadCompaniesList();
		}
	}, [client]); // eslint-disable-line react-hooks/exhaustive-deps

	const loadCompaniesList = () => {
		setCompaniesLoading(true);
		API_Service.get(req_report_placestracking_companies, axiosDefaultConfig(client))
			.then(({ data }) => {
				const { result, extraData } = data;
				if (result === 1) {
					setCompaniesList(extraData);
				} else {
					enqueueSnackbar(data.message || 'Error', { variant: 'error' });
				}
				setCompaniesLoading(false);
			})
			.catch(() => {
				enqueueSnackbar('Network error', { variant: 'error' });
				setCompaniesLoading(false);
			});
	};

	const getReportData = () => {
		if (!selectedCompany) {
			enqueueSnackbar(language.company || 'Please select a company', { variant: 'warning' });
			return;
		}
		setLoading(true);
		const dateFrom = format(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1), 'yyyy-MM-dd');
		const dateTo = format(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 1), 'yyyy-MM-dd');

		const formdata = new FormData();
		formdata.append('client', selectedCompany);
		formdata.append('date_from', dateFrom);
		formdata.append('date_to', dateTo);

		API_Service.post(req_report_placestracking, formdata, axiosDefaultConfig(client))
			.then(({ data }) => {
				if (data.result === 1) {
					setReportData(data.extraData);
				} else {
					setReportData([]);
					enqueueSnackbar(data.message || language.no_data || 'No data', { variant: 'warning' });
				}
				setLoading(false);
			})
			.catch(() => {
				enqueueSnackbar('Network error', { variant: 'error' });
				setLoading(false);
			});
	};

	const handleCompanySelect = (event, value) => {
		if (value) {
			setSelectedCompany(value.company_code);
		} else {
			setSelectedCompany(empty);
		}
	};

	const columns = [
		{
			name: "check_in_date",
			label: language.check_in_date || "Check In Date",
			options: {
				display: true,
				customBodyRender: (value) => {
					return value ? moment(value).format('DD/MM/YYYY hh:mmA') : '';
				}
			}
		},
		{
			name: "places",
			label: language.places || "Places",
			options: {
				display: true
			}
		},
		{
			name: "agent_code",
			label: language.agent_code || "Agent Code",
			options: {
				display: true
			}
		}
	];

	const tableOptions = {
		selectableRows: 'none',
		responsive: 'standard',
		rowsPerPage: 20,
		rowsPerPageOptions: [20, 50, 100],
		download: false,
		print: false,
		filter: false,
		search: false
	};

	if (companiesLoading) {
		return (
			<div>
				<Paper className={classes.filterPaper} elevation={1}>
					<div className={classes.skeletonFilterRow}>
						<Skeleton variant="rect" width={300} height={36} animation="wave" />
						<Skeleton variant="rect" width={300} height={36} animation="wave" />
						<Skeleton variant="rect" width={80} height={36} animation="wave" />
					</div>
				</Paper>
				<div className={classes.spacer} />
				<Paper elevation={1}>
					<div className={classes.skeletonTableHeader}>
						<Skeleton variant="text" width="25%" height={32} animation="wave" />
						<Skeleton variant="text" width="40%" height={32} animation="wave" />
						<Skeleton variant="text" width="20%" height={32} animation="wave" />
					</div>
					{[...Array(6)].map((_, i) => (
						<div key={i} className={classes.skeletonRow}>
							<Skeleton variant="text" width="25%" height={20} animation="wave" />
							<Skeleton variant="text" width="40%" height={20} animation="wave" />
							<Skeleton variant="text" width="20%" height={20} animation="wave" />
						</div>
					))}
				</Paper>
			</div>
		);
	}

	return (
		<div>
			<Paper className={classes.filterPaper} elevation={1}>
				<div className={classes.viewInRow}>
					<Autocomplete
						className={classes.companyAutocomplete}
						classes={{ listbox: classes.darkListbox }}
						options={companiesList}
						getOptionLabel={(option) => option.company_name}
						getOptionSelected={(option, value) => option.company_code === value.company_code}
						onChange={handleCompanySelect}
						size="small"
						renderInput={(params) => (
							<TextField
								{...params}
								placeholder={language.company || 'Company'}
								variant="outlined"
							/>
						)}
					/>
					<MuiPickersUtilsProvider utils={DateFnsUtils}>
						<DatePicker
							className={classes.customDatePicker}
							inputVariant="outlined"
							views={['month', 'year']}
							value={selectedMonth}
							onChange={(date) => setSelectedMonth(date)}
						/>
					</MuiPickersUtilsProvider>
					<Button
						className={classes.searchButton}
						size="small"
						color="primary"
						variant="contained"
						onClick={getReportData}
						disabled={loading}>
						<Typography className={commonStyle.searchBtnFont}>{language.view || 'View'}</Typography>
						{loading && <CircularProgress size={20} />}
					</Button>
				</div>
			</Paper>
			{loading && <LinearLoading />}
			<div className={classes.spacer} />
			<MUIDataTable
				title={language.google_location_tracking}
				data={reportData}
				columns={columns}
				options={tableOptions}
			/>
		</div>
	);
}
